import { cn } from '@merritrac/shared/components';
import {
  Building,
  Building2,
  ClipboardList,
  Clock,
  Contact,
  Cpu,
  FileText,
  LayoutDashboard,
  ListChecks,
  LogOut,
  MessageSquareMore,
  Settings,
  Users,
} from 'lucide-react';
import { useCallback } from 'react';
import { NavLink, useNavigate } from 'react-router';

import { useAuth } from '../../lib/auth.js';

import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';

interface NavItem {
  label: string;
  to: string;
  icon: LucideIcon;
}

const mainNav: NavItem[] = [
  { label: 'Dashboard', to: '/', icon: LayoutDashboard },
  { label: 'Service Orders', to: '/service-orders', icon: FileText },
  { label: 'Clients', to: '/clients', icon: Building2 },
  { label: 'Properties', to: '/properties', icon: Building },
  { label: 'Units', to: '/units', icon: Cpu },
  { label: 'Templates', to: '/checklist-templates', icon: ListChecks },
  { label: 'Checklists', to: '/checklists', icon: ClipboardList },
  { label: 'Time Tracking', to: '/time-tracking', icon: Clock },
  { label: 'Contacts', to: '/contacts', icon: Contact },
  { label: 'Requests', to: '/service-requests', icon: MessageSquareMore },
];

const adminNav: NavItem[] = [
  { label: 'Users', to: '/users', icon: Users },
  { label: 'Settings', to: '/settings', icon: Settings },
];

function SidebarLink({ item }: { item: NavItem }): ReactElement {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      className={({ isActive }): string =>
        cn(
          'flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium',
          'transition-all duration-[var(--dur)] ease-[var(--ease)]',
          isActive
            ? 'bg-sidebar-active text-sidebar-text-active shadow-[var(--sidebar-glow)]'
            : 'text-sidebar-text hover:bg-sidebar-hover hover:text-sidebar-text-active',
        )
      }
    >
      <Icon size={18} strokeWidth={1.8} />
      <span>{item.label}</span>
    </NavLink>
  );
}

export function Sidebar(): ReactElement {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = useCallback((): void => {
    logout();
    void navigate('/login');
  }, [logout, navigate]);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 flex flex-col',
        'w-[var(--sidebar-w)]',
        'bg-[var(--sidebar-bg)] backdrop-blur-[var(--glass-blur-heavy)]',
        'border-r border-[var(--sidebar-border)]',
      )}
    >
      {/* Logo */}
      <div className="flex items-center gap-3 px-5 h-[var(--header-h)] border-b border-[var(--sidebar-border)]">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-primary/20 shadow-[0_0_12px_var(--primary)]">
          <svg
            viewBox="0 0 32 32"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            className="w-5 h-5 text-primary"
          >
            <line x1="16" y1="4" x2="16" y2="28" />
            <line x1="4" y1="16" x2="28" y2="16" />
            <line x1="7" y1="7" x2="25" y2="25" />
            <line x1="25" y1="7" x2="7" y2="25" />
          </svg>
        </div>
        <span className="text-sidebar-text-active font-semibold text-base tracking-tight">
          MerriTrac
        </span>
      </div>

      {/* Main navigation */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto glass-scroll">
        <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-section">
          Operations
        </p>
        {mainNav.map((item) => (
          <SidebarLink key={item.to} item={item} />
        ))}

        <div className="my-4 border-t border-[var(--sidebar-border)]" />

        <p className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider text-sidebar-section">
          Administration
        </p>
        {adminNav.map((item) => (
          <SidebarLink key={item.to} item={item} />
        ))}
      </nav>

      {/* Footer with user info and logout */}
      <div className="px-3 py-3 border-t border-[var(--sidebar-border)] space-y-2">
        {user && (
          <div className="px-2">
            <p className="text-xs font-medium text-sidebar-text-active truncate">{user.email}</p>
            <p className="text-xs text-sidebar-section capitalize">{user.role}</p>
          </div>
        )}
        <button
          type="button"
          onClick={handleLogout}
          className={cn(
            'flex items-center gap-3 w-full px-3 py-2 rounded-lg text-sm font-medium',
            'text-sidebar-text hover:bg-sidebar-hover hover:text-danger',
            'transition-all duration-[var(--dur)] ease-[var(--ease)]',
            'cursor-pointer',
          )}
        >
          <LogOut size={18} strokeWidth={1.8} />
          <span>Sign out</span>
        </button>
      </div>
    </aside>
  );
}
